/**
 * Celebration — stage-completion wow moment.
 *
 * Three layers:
 *   1. A pop-in header ("أَحْسَنْتَ!" / nice!) with the earned stars.
 *   2. A CSS-only confetti burst using 24 pre-calculated particle paths.
 *   3. A soft haptic pulse on devices that support navigator.vibrate.
 *
 * Self-contained — uses inline keyframes so it works even before the
 * main Tailwind CSS layer has loaded.
 */

import { useEffect, useMemo } from 'preact/hooks';

interface Props {
  stars: number; // 0-3
  scoreText?: string; // e.g. "5/5"
  title?: string; // override default "أَحْسَنْتَ!"
  subtitleEn?: string;
}

const CONFETTI_COLORS = ['#FF7A59', '#3BB4D1', '#5CC98D', '#FFD45E', '#B94A8C', '#E85A3B'];

export function Celebration({ stars, scoreText, title, subtitleEn }: Props) {
  // Haptic feedback — only fires once on mount, graceful no-op otherwise.
  useEffect(() => {
    try {
      navigator.vibrate?.(stars >= 2 ? [30, 40, 80] : 30);
    } catch {
      /* navigator.vibrate unsupported */
    }
  }, []);

  // Stable confetti particle list — 24 pieces scattered across the viewport.
  const particles = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        left: (i * 37 + 5) % 100,
        delay: (i % 6) * 0.05,
        duration: 1.5 + ((i * 13) % 20) / 10,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        rotate: (i * 47) % 360,
        size: 6 + ((i * 7) % 10),
      })),
    [],
  );

  const titleAr = title ?? (stars >= 3 ? 'رَائِعٌ!' : stars >= 1 ? 'أَحْسَنْتَ!' : 'حَاوِلْ مَرَّةً أُخْرَى');
  const titleEn =
    subtitleEn ??
    (stars >= 3 ? 'Amazing!' : stars >= 1 ? 'Nice work!' : 'Try again!');

  return (
    <div class="fixed inset-0 z-40 flex items-center justify-center pointer-events-none">
      {/* Confetti particles */}
      <div class="absolute inset-0 overflow-hidden">
        {particles.map((p, idx) => (
          <span
            key={idx}
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-10%',
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size * 1.4}px`,
              background: p.color,
              borderRadius: '2px',
              transform: `rotate(${p.rotate}deg)`,
              animation: `huroof-confetti ${p.duration}s linear ${p.delay}s 1 forwards`,
              willChange: 'transform, opacity',
            }}
          />
        ))}
      </div>

      {/* Centered card */}
      <div
        class="relative bg-white rounded-3xl shadow-2xl px-10 py-8 text-center flex flex-col items-center gap-3 animate-pop"
        style={{ maxWidth: '85%' }}
      >
        <div class="text-5xl" aria-hidden="true">
          {stars >= 3 ? '🎉' : stars >= 1 ? '🌟' : '🌱'}
        </div>
        <p class="text-3xl font-display text-coral">{titleAr}</p>
        <p class="text-sm text-ink/50" dir="ltr">
          {titleEn}
        </p>
        {scoreText && <p class="text-xl font-display mt-1">{scoreText}</p>}
        <p class="text-3xl mt-1">
          {[1, 2, 3].map((n) => (
            <span class={n <= stars ? 'text-sun' : 'opacity-20'}>⭐</span>
          ))}
        </p>
      </div>
    </div>
  );
}
